import { EnvType } from "../services";
import { IApiClient } from "../api-client";
import { GenericResponseStd } from "../Types/generic-response-std";
import { CheckRequestsExistsResponse } from "../Types/check-requests-exists-response";
import { CreatePersonalBackupsRequest } from "../Types/create-personal-backups-request";
import { DbIngestConfigColumnSetInfos } from "../Types/db-ingest-config-column-set-infos";
import { DbIngestConfigPackInfos } from "../Types/db-ingest-config-pack-infos";
import { DbIngestConfigPackPlaceholderRowInfos } from "../Types/db-ingest-config-pack-placeholder-row-infos";
import { DeleteObjectRequest } from "../Types/delete-object-request";
import { GetAsperaConfigurationResponse } from "../Types/get-aspera-configuration-response";
import { GetConfigPackLayoutResponse } from "../Types/get-config-pack-layout-response";
import { GetConfigPackListResponse } from "../Types/get-config-pack-list-response";
import { GetIngestMetadataFieldsResponse } from "../Types/get-ingest-metadata-fields-response";
import { GetLabProviderResponse } from "../Types/get-lab-provider-response";
import { GetMyPersonalBackupsResponse } from "../Types/get-my-personal-backups-response";
import { GetPlaceholderResponse } from "../Types/get-placeholder-response";
import { GetPlaceholdersResponse } from "../Types/get-placeholders-response";
import { GetRejectionsByProviderResponse } from "../Types/get-rejections-by-provider-response";
import { GetRejectionsRatesResponse } from "../Types/get-rejections-rates-response";
import { GetRejectionsResponse } from "../Types/get-rejections-response";
import { GetRequestsResponse } from "../Types/get-requests-response";
import { NormalizerProcessRequest } from "../Types/normalizer-process-request";
import { UpdatePersonalBackupsRequest } from "../Types/update-personal-backups-request";
import { UpdatePlaceholderByIdsRequest } from "../Types/update-placeholder-by-ids-request";
import { NormalizerProcessResponse } from "../Types/normalizer-process-response";
import { NormalizerInspectResponse } from "../Types/normalizer-inspect-response";
import { GetMyPersonalBackupResponse } from "../Types/get-my-personal-backup-response";
import { GetLabProviderInfosResponse } from "../Types/get-lab-provider-infos-response";
import { GetFilteredPlaceholdersRequest } from "../Types/get-filtered-placeholders-request";
import { MovePlaceholderByIdsRequest } from "../Types/move-placeholder-by-ids-request";

interface IIngestApiClient {
  GetRequests(): Promise<GetRequestsResponse>;
  GetMyRequests(): Promise<GetRequestsResponse>;
  CheckRequestsExists(titleId: number): Promise<CheckRequestsExistsResponse>;
  DeleteRequest(request: DeleteObjectRequest): Promise<GenericResponseStd>;
  AssignRequestToProvider(id: number, providerId: number): Promise<GenericResponseStd>;
  ReprocessRequestAfterAutomatedRejection(id: number): Promise<GenericResponseStd>;
  GetRejections(): Promise<GetRejectionsResponse>;
  GetRejectionsByProviderId(labProviderId: number): Promise<GetRejectionsByProviderResponse>;
  GetRejectionsRatings(maxDays: number): Promise<GetRejectionsRatesResponse>;
  GetProviderById(id: number): Promise<GetLabProviderInfosResponse>;
  GetProviders(): Promise<GetLabProviderResponse>;
  ReloadProviders(): Promise<GenericResponseStd>;
  GetIngestMetadataFields(): Promise<GetIngestMetadataFieldsResponse>;
  GetAsperaSettings(): Promise<GetAsperaConfigurationResponse>;
  GetConfigPackList(): Promise<GetConfigPackListResponse>;
  GetConfigPackById(id: number): Promise<GetConfigPackLayoutResponse>;
  GetPlaceholdersByPackId(packId: number): Promise<GetConfigPackLayoutResponse>;
  CreateConfigPack(pack: DbIngestConfigPackInfos): Promise<GenericResponseStd>;
  DeleteConfigPackById(id: number): Promise<GenericResponseStd>;
  GetPlaceholders(): Promise<GetPlaceholdersResponse>;
  GetFilteredPlaceholders(request: GetFilteredPlaceholdersRequest): Promise<GetPlaceholdersResponse>;
  GetPlaceholderById(placeholderId: number): Promise<GetPlaceholderResponse>;
  CreatePlaceholder(packId: number, request: DbIngestConfigPackPlaceholderRowInfos): Promise<GenericResponseStd>;
  UpdatePlaceholder(request: DbIngestConfigPackPlaceholderRowInfos): Promise<GenericResponseStd>;
  UpdatePlaceholders(request: UpdatePlaceholderByIdsRequest): Promise<GenericResponseStd>;
  MovePlaceholders(request: MovePlaceholderByIdsRequest): Promise<GenericResponseStd>;
  DeletePlaceholderById(placeholderId: number): Promise<GenericResponseStd>;
  GetMyPersonalBackups(): Promise<GetMyPersonalBackupsResponse>;
  GetMyPersonalBackupById(id: number): Promise<GetMyPersonalBackupResponse>;
  CreatePersonalBackups(request: CreatePersonalBackupsRequest): Promise<GenericResponseStd>;
  UpdatePersonalBackups(request: UpdatePersonalBackupsRequest): Promise<GenericResponseStd>;
  DeletePersonalBackups(id: number): Promise<GenericResponseStd>;
  CreateIngestConfigColumnSet(column: DbIngestConfigColumnSetInfos): Promise<GenericResponseStd>;
  UpdateIngestConfigColumnSet(column: DbIngestConfigColumnSetInfos): Promise<GenericResponseStd>;
  NormalizerGetRequests(): Promise<GetRequestsResponse>;
  NormalizerInspect(objectId: number): Promise<NormalizerInspectResponse>;
  NormalizeProcess(request: NormalizerProcessRequest): Promise<NormalizerProcessResponse>;
}

export default class IngestApiClient implements IIngestApiClient {
  readonly servicePort = 9502;
  readonly serviceBaseUrl = "ingest-service/api";

  ingestApiClient: IApiClient;

  constructor(ingestApiClient: IApiClient, envType: EnvType) {
    this.ingestApiClient = ingestApiClient;
    if (envType === "local") this.ingestApiClient.client.defaults.baseURL += `${this.servicePort}/`;
  }

  async GetRequests(): Promise<GetRequestsResponse> {
    return await this.ingestApiClient.get(`${this.serviceBaseUrl}/requests`);
  }
  async GetMyRequests(): Promise<GetRequestsResponse> {
    return await this.ingestApiClient.get(`${this.serviceBaseUrl}/requests/my`);
  }
  async CheckRequestsExists(titleId: number): Promise<CheckRequestsExistsResponse> {
    return await this.ingestApiClient.get(`${this.serviceBaseUrl}/requests/existing/${titleId}`);
  }
  async DeleteRequest(request: DeleteObjectRequest): Promise<GenericResponseStd> {
    return await this.ingestApiClient.delete(`${this.serviceBaseUrl}/requests`, request);
  }
  async AssignRequestToProvider(id: number, providerId: number): Promise<GenericResponseStd> {
    return await this.ingestApiClient.put(`${this.serviceBaseUrl}/requests/${id}/assign-to-provider/${providerId}`);
  }
  async ReprocessRequestAfterAutomatedRejection(id: number): Promise<GenericResponseStd> {
    return await this.ingestApiClient.put(`${this.serviceBaseUrl}/requests/${id}/reprocess`);
  }
  async GetRejections(): Promise<GetRejectionsResponse> {
    return await this.ingestApiClient.get(`${this.serviceBaseUrl}/rejections`);
  }
  async GetRejectionsByProviderId(labProviderId: number): Promise<GetRejectionsByProviderResponse> {
    return await this.ingestApiClient.get(`${this.serviceBaseUrl}/rejections/providers/${labProviderId}`);
  }
  async GetRejectionsRatings(maxDays: number = 180): Promise<GetRejectionsRatesResponse> {
    return await this.ingestApiClient.get(`${this.serviceBaseUrl}/rejections/ratings/${maxDays}`);
  }
  async GetProviderById(id: number): Promise<GetLabProviderInfosResponse> {
    return await this.ingestApiClient.get(`${this.serviceBaseUrl}/provider/${id}`);
  }
  async GetProviders(): Promise<GetLabProviderResponse> {
    return await this.ingestApiClient.get(`${this.serviceBaseUrl}/providers`);
  }
  async ReloadProviders(): Promise<GenericResponseStd> {
    return await this.ingestApiClient.post(`${this.serviceBaseUrl}/providers/reload`, null);
  }
  async GetIngestMetadataFields(): Promise<GetIngestMetadataFieldsResponse> {
    return await this.ingestApiClient.get(`${this.serviceBaseUrl}/config/metadata/fields`);
  }
  async GetAsperaSettings(): Promise<GetAsperaConfigurationResponse> {
    return await this.ingestApiClient.get(`${this.serviceBaseUrl}/config/aspera/settings`);
  }
  async GetConfigPackList(): Promise<GetConfigPackListResponse> {
    return await this.ingestApiClient.get(`${this.serviceBaseUrl}/config/packs`);
  }
  async GetConfigPackById(packId: number): Promise<GetConfigPackLayoutResponse> {
    return await this.ingestApiClient.get(`${this.serviceBaseUrl}/config/packs/${packId}`);
  }
  async GetPlaceholdersByPackId(packId: number): Promise<GetConfigPackLayoutResponse> {
    return await this.ingestApiClient.get(`${this.serviceBaseUrl}/config/packs/${packId}/placeholders`);
  }
  async CreateConfigPack(pack: DbIngestConfigPackInfos): Promise<GenericResponseStd> {
    return await this.ingestApiClient.post(`${this.serviceBaseUrl}/config/packs`, pack);
  }
  async DeleteConfigPackById(id: number): Promise<GenericResponseStd> {
    return await this.ingestApiClient.delete(`${this.serviceBaseUrl}/config/packs/${id}`);
  }
  async GetPlaceholders(): Promise<GetPlaceholdersResponse> {
    return await this.ingestApiClient.get(`${this.serviceBaseUrl}/config/packs/placeholders`);
  }
  async GetFilteredPlaceholders(request: GetFilteredPlaceholdersRequest): Promise<GetPlaceholdersResponse> {
    return await this.ingestApiClient.post(`${this.serviceBaseUrl}/config/packs/placeholders/filtered`, request);
  }
  async GetPlaceholderById(placeholderId: number): Promise<GetPlaceholderResponse> {
    return await this.ingestApiClient.get(`${this.serviceBaseUrl}/config/packs/placeholders/${placeholderId}`);
  }
  async CreatePlaceholder(packId: number, request: DbIngestConfigPackPlaceholderRowInfos): Promise<GenericResponseStd> {
    return await this.ingestApiClient.post(`${this.serviceBaseUrl}/config/packs/${packId}/placeholders`, request);
  }
  async UpdatePlaceholder(request: DbIngestConfigPackPlaceholderRowInfos): Promise<GenericResponseStd> {
    return await this.ingestApiClient.put(`${this.serviceBaseUrl}/config/packs/placeholders`, request);
  }
  async UpdatePlaceholders(request: UpdatePlaceholderByIdsRequest): Promise<GenericResponseStd> {
    return await this.ingestApiClient.put(`${this.serviceBaseUrl}/config/packs/placeholders/multiple`, request);
  }
  async MovePlaceholders(request: MovePlaceholderByIdsRequest): Promise<GenericResponseStd> {
    return await this.ingestApiClient.put(`${this.serviceBaseUrl}/config/packs/placeholders/move-multiple`, request);
  }
  async DeletePlaceholderById(placeholderId: number): Promise<GenericResponseStd> {
    return await this.ingestApiClient.delete(`${this.serviceBaseUrl}/config/packs/placeholders/${placeholderId}`);
  }
  async GetMyPersonalBackups(): Promise<GetMyPersonalBackupsResponse> {
    return await this.ingestApiClient.get(`${this.serviceBaseUrl}/config/personal-backups`);
  }
  async GetMyPersonalBackupById(id: number): Promise<GetMyPersonalBackupResponse> {
    return await this.ingestApiClient.get(`${this.serviceBaseUrl}/config/personal-backups/${id}`);
  }
  async CreatePersonalBackups(request: CreatePersonalBackupsRequest): Promise<GenericResponseStd> {
    return await this.ingestApiClient.post(`${this.serviceBaseUrl}/config/personal-backups`, request);
  }
  async UpdatePersonalBackups(request: UpdatePersonalBackupsRequest): Promise<GenericResponseStd> {
    return await this.ingestApiClient.put(`${this.serviceBaseUrl}/config/personal-backups`, request);
  }
  async DeletePersonalBackups(id: number): Promise<GenericResponseStd> {
    return await this.ingestApiClient.delete(`${this.serviceBaseUrl}/config/personal-backups/${id}`);
  }
  async CreateIngestConfigColumnSet(column: DbIngestConfigColumnSetInfos): Promise<GenericResponseStd> {
    return await this.ingestApiClient.post(`${this.serviceBaseUrl}/config/column`, column);
  }
  async UpdateIngestConfigColumnSet(column: DbIngestConfigColumnSetInfos): Promise<GenericResponseStd> {
    return await this.ingestApiClient.put(`${this.serviceBaseUrl}/config/column`, column);
  }
  async NormalizerGetRequests(): Promise<GetRequestsResponse> {
    return await this.ingestApiClient.get(`${this.serviceBaseUrl}/normalizer/requests`);
  }
  async NormalizerInspect(objectId: number): Promise<NormalizerInspectResponse> {
    return await this.ingestApiClient.get(`${this.serviceBaseUrl}/normalizer/inspect/${objectId}`);
  }
  async NormalizeProcess(request: NormalizerProcessRequest): Promise<NormalizerProcessResponse> {
    return await this.ingestApiClient.post(`${this.serviceBaseUrl}/normalizer/process`, request);
  }
  async RemindProvider(assetId: number): Promise<GenericResponseStd> {
    return await this.ingestApiClient.post(`${this.serviceBaseUrl}/requests/${assetId}/remind-provider`);
  }
  async UploadAndReplaceRequestMetadata(
    objectId: number,
    fileToUpload: File,
    abortSignal?: AbortSignal,
    onUploadProgressCallback?: (progressEvent: any) => void
  ): Promise<GenericResponseStd> {
    const formData = new FormData();
    formData.append("file", fileToUpload);

    return await this.ingestApiClient.postUploadFormData(
      `${this.serviceBaseUrl}/requests/${objectId}/update-replace-metadata`,
      formData,
      abortSignal,
      onUploadProgressCallback
    );
  }

  //------Utils------
  GetApiUrl(): string {
    return this.ingestApiClient.client.defaults.baseURL + this.serviceBaseUrl;
  }
}
